import React, { useCallback, useEffect, useState } from 'react';
import { HiRefresh, HiExclamationCircle, HiInformationCircle } from 'react-icons/hi';
import apiClient from '../api/client';
import { useWbNavigate } from './WbRouteContext';
import WbShell, { WbTopBar } from './WbShell';

function formatDt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function LevelBadge({ level }) {
  const isError = level === 'error';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        isError
          ? 'bg-[var(--wb-danger-bg)] text-[var(--wb-danger)]'
          : 'bg-amber-50 text-amber-800'
      }`}
    >
      {isError ? 'error' : 'warn'}
    </span>
  );
}

function OriginBadge({ origin }) {
  return (
    <span className="text-xs text-[var(--wb-text-muted)]">
      {origin === 'script' ? 'скрипт' : 'runtime'}
    </span>
  );
}

export default function WbAdminScriptErrors() {
  const navigate = useWbNavigate();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    days: 7,
    level: 'all',
    source: '',
    origin: 'all',
    q: '',
    critical: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const params = new URLSearchParams({
        days: String(filters.days),
        level: filters.level,
        origin: filters.origin,
        page: String(pagination.page),
        limit: String(pagination.limit),
        critical: filters.critical ? '1' : '0',
      });
      if (filters.source) params.set('source', filters.source);
      if (filters.q.trim()) params.set('q', filters.q.trim());

      const [listRes, sumRes] = await Promise.all([
        apiClient.get(`/admin/script-errors?${params}`),
        apiClient.get(
          `/admin/script-errors/summary?days=${filters.days}&critical=${filters.critical ? '1' : '0'}`,
        ),
      ]);

      setItems(Array.isArray(listRes.data?.items) ? listRes.data.items : []);
      setSources(Array.isArray(listRes.data?.sources) ? listRes.data.sources : []);
      setPagination((prev) => ({
        ...prev,
        ...(listRes.data?.pagination || {}),
      }));
      setSummary(sumRes.data || null);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        return;
      }
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, navigate]);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    load();
  }, [load, navigate]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  if (forbidden) {
    return (
      <WbShell>
        <WbTopBar
          title="Ошибки скриптов"
          subtitle="Только для администратора"
          backTo={() => navigate('/dashboard')}
        />
        <main className="max-w-3xl mx-auto p-6">
          <div className="wb-card p-8 text-center text-[var(--wb-danger)]">
            Доступ запрещён: требуется роль admin
          </div>
        </main>
      </WbShell>
    );
  }

  const errorCnt = (summary?.script || [])
    .filter((r) => r.level === 'error')
    .reduce((s, r) => s + Number(r.cnt || 0), 0)
    + (summary?.runtime || [])
      .filter((r) => r.level === 'error')
      .reduce((s, r) => s + Number(r.cnt || 0), 0);
  const warnCnt = (summary?.script || [])
    .filter((r) => r.level === 'warn')
    .reduce((s, r) => s + Number(r.cnt || 0), 0)
    + (summary?.runtime || [])
      .filter((r) => r.level === 'warn')
      .reduce((s, r) => s + Number(r.cnt || 0), 0);

  return (
    <WbShell>
      <WbTopBar
        title="Ошибки скриптов"
        subtitle="Только критичные сбои, влияющие на работу сервиса"
        backTo={() => navigate('/dashboard')}
      />

      <main className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="wb-card p-4">
            <p className="text-xs text-[var(--wb-text-muted)]">Ошибки · {filters.days} дн.</p>
            <p className="text-2xl font-semibold text-[var(--wb-danger)]">{errorCnt}</p>
          </div>
          <div className="wb-card p-4">
            <p className="text-xs text-[var(--wb-text-muted)]">Предупреждения</p>
            <p className="text-2xl font-semibold text-amber-700">{warnCnt}</p>
          </div>
          <div className="wb-card p-4 col-span-2 sm:col-span-1">
            <p className="text-xs text-[var(--wb-text-muted)]">Записей на странице</p>
            <p className="text-2xl font-semibold">{pagination.total}</p>
          </div>
        </div>

        <div className="wb-card p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Дней</span>
              <select
                className="wb-input"
                value={filters.days}
                onChange={(e) => setFilter('days', Number(e.target.value))}
              >
                {[1, 3, 7, 14, 30].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Режим</span>
              <select
                className="wb-input"
                value={filters.critical ? 'critical' : 'all'}
                onChange={(e) => setFilter('critical', e.target.value === 'critical')}
              >
                <option value="critical">только критичные</option>
                <option value="all">все ошибки (шум)</option>
              </select>
            </label>
            {!filters.critical ? (
              <label className="text-sm">
                <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Уровень</span>
                <select
                  className="wb-input"
                  value={filters.level}
                  onChange={(e) => setFilter('level', e.target.value)}
                >
                  <option value="all">все</option>
                  <option value="error">error</option>
                  <option value="warn">warn</option>
                </select>
              </label>
            ) : null}
            <label className="text-sm">
              <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Источник</span>
              <select
                className="wb-input min-w-[160px]"
                value={filters.origin}
                onChange={(e) => setFilter('origin', e.target.value)}
              >
                <option value="all">скрипты + runtime</option>
                <option value="script">только скрипты</option>
                <option value="runtime">только runtime</option>
              </select>
            </label>
            <label className="text-sm flex-1 min-w-[140px]">
              <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Скрипт / сервис</span>
              <select
                className="wb-input w-full"
                value={filters.source}
                onChange={(e) => setFilter('source', e.target.value)}
              >
                <option value="">все</option>
                {sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="text-sm flex-1 min-w-[160px]">
              <span className="block text-xs text-[var(--wb-text-muted)] mb-1">Поиск</span>
              <input
                className="wb-input w-full"
                value={filters.q}
                onChange={(e) => setFilter('q', e.target.value)}
                placeholder="текст ошибки…"
              />
            </label>
            <button type="button" onClick={load} className="wb-btn-secondary" disabled={loading}>
              <HiRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Обновить
            </button>
          </div>
          <p className="text-xs text-[var(--wb-text-muted)] flex items-start gap-1.5">
            <HiInformationCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              По умолчанию скрыты: rate-limit 429, fallback расхода, демо-кабинет, пустой бюджет,
              auction 404, правила удалённых клиентов. Время — МСК.
            </span>
          </p>
        </div>

        <div className="wb-card overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[var(--wb-text-muted)]">Загрузка…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-[var(--wb-text-muted)] flex flex-col items-center gap-2">
              <HiExclamationCircle className="w-8 h-8 opacity-40" />
              За выбранный период ошибок не найдено
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[var(--wb-surface-muted)] text-[var(--wb-text-muted)]">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-medium">Дата</th>
                    <th className="px-3 py-2.5 text-left font-medium">Уровень</th>
                    <th className="px-3 py-2.5 text-left font-medium">Источник</th>
                    <th className="px-3 py-2.5 text-left font-medium">Сообщение</th>
                    <th className="px-3 py-2.5 text-left font-medium">Тип</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--wb-border)]">
                  {items.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr
                        className="hover:bg-[var(--wb-surface-muted)]/60 cursor-pointer"
                        onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                      >
                        <td className="px-3 py-2 whitespace-nowrap align-top">
                          {formatDt(row.created_at)}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <LevelBadge level={row.level} />
                        </td>
                        <td className="px-3 py-2 align-top font-medium whitespace-nowrap">
                          {row.source}
                          {row.client_id != null ? (
                            <span className="block text-xs font-normal text-[var(--wb-text-muted)]">
                              client #{row.client_id}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top max-w-xl">
                          <span className="line-clamp-2">{row.message || '—'}</span>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <OriginBadge origin={row.origin} />
                        </td>
                      </tr>
                      {expandedId === row.id && row.details ? (
                        <tr className="bg-[var(--wb-surface-muted)]/40">
                          <td colSpan={5} className="px-3 py-3">
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all text-[var(--wb-text-secondary)]">
                              {JSON.stringify(row.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pagination.totalPages > 1 ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-[var(--wb-text-muted)]">
              Стр. {pagination.page} из {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="wb-btn-secondary"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Назад
              </button>
              <button
                type="button"
                className="wb-btn-secondary"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Далее
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </WbShell>
  );
}
